import React from 'react';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AdminOverviewPage = loadable(() => import(/* webpackChunkName: "AdminOverviewPage" */ '../containers/AdminOverviewPage/AdminOverviewPage'));
const BannerAdminPage = loadable(() => import(/* webpackChunkName: "BannerAdminPage" */ '../containers/BannerAdminPage/BannerAdminPage'));
const ListingAdminPage = loadable(() => import(/* webpackChunkName: "ListingAdminPage" */ '../containers/ListingAdminPage/ListingAdminPage'));
const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const PartnerPage = loadable(() => import(/* webpackChunkName: "PartnerPage" */ '../containers/PartnerPage/PartnerPage'));
const AngreskjemaPage = loadable(() => import(/* webpackChunkName: "AngreskjemaPage" */ '../containers/AngreskjemaPage/AngreskjemaPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const MakeOfferPage = loadable(() => import(/* webpackChunkName: "MakeOfferPage" */ '../containers/MakeOfferPage/MakeOfferPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const FavoriteListingsPage = loadable(() => import(/* webpackChunkName: "FavoriteListingsPage" */ '../containers/FavoriteListingsPage/FavoriteListingsPage'));
const ManageAccountPage = loadable(() => import(/* webpackChunkName: "ManageAccountPage" */ '../containers/ManageAccountPage/ManageAccountPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const RequestQuotePage = loadable(() => import(/* webpackChunkName: "RequestQuotePage" */ '../containers/RequestQuotePage/RequestQuotePage'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const SearchPageMapFallback = loadable(() => import(/* webpackChunkName: "SearchPageMapFallback" */ '../containers/SearchPage/SearchPageMapFallback'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const NoAccessPage = loadable(() => import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'));
const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ '../containers/AboutPage/AboutPage'));
const BliMedlemPage = loadable(() => import(/* webpackChunkName: "BliMedlemPage" */ '../containers/BliMedlemPage/BliMedlemPage'));
const UserTermsPage = loadable(() => import(/* webpackChunkName: "UserTermsPage" */ '../containers/UserTermsPage/UserTermsPage'));
const FaqPage = loadable(() => import(/* webpackChunkName: "FaqPage" */ '../containers/FaqPage/FaqPage'));
const HowToBuyPage = loadable(() => import(/* webpackChunkName: "HowToBuyPage" */ '../containers/HowToBuyPage/HowToBuyPage'));
const HowToSellPage = loadable(() => import(/* webpackChunkName: "HowToSellPage" */ '../containers/HowToSellPage/HowToSellPage'));
const CookiesPage = loadable(() => import(/* webpackChunkName: "CookiesPage" */ '../containers/CookiesPage/CookiesPage'));
const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/ContactPage/ContactPage'));
const VurderingerPage = loadable(() => import(/* webpackChunkName: "VurderingerPage" */ '../containers/VurderingerPage/VurderingerPage'));
const MagasinPage = loadable(() => import(/* webpackChunkName: "MagasinPage" */ '../containers/MagasinPage/MagasinPage'));
const MagasinArticlePage = loadable(() => import(/* webpackChunkName: "MagasinArticlePage" */ '../containers/MagasinArticlePage/MagasinArticlePage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'ManageAccountPage'
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;
// The old Console-hosted terms page, retired: the terms live at /brukervilkar.
// The server answers /terms-of-service with a 301 as well (server/index.js).
const RedirectToUserTermsPage = () => <NamedRedirect name="UserTermsPage" />;
// The campaign page's first address, before /kom-i-gang (Guro, 9 October).
// Ads and links may still use it, so the query (UTM, fbclid, ?fra=footer)
// comes along for the sign_up event. The server answers with a 301 as well.
const RedirectToKomIGangPage = () => {
  const { search } = useLocation();
  return <NamedRedirect name="BliMedlemPage" search={search} />;
};

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  // ZUPRIZE only ships the grid SearchPage variant after the redesign
  // (Figma node 235:839). If an operator still sets variantType=map in
  // Sharetribe Console, render a fallback page explaining that the map
  // view isn't supported instead of breaking the route entirely.
  const isSearchPageWithMap = layoutConfig.searchPage?.variantType === 'map';
  const SearchPage = isSearchPageWithMap ? SearchPageMapFallback : SearchPageWithGrid;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel'
    ? ListingPageCarousel
    : ListingPageCoverPhoto;

  const isPrivateMarketplace = accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};
  
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    // NOTE: when the private marketplace feature is enabled, the '/s' route is disallowed by the robots.txt resource.
    // If you add new routes that start with '/s*' (e.g. /support), you should add them to the robotsPrivateMarketplace.txt file.
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeMapLibraryLoading: false,
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithListingType',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeMapLibraryLoading: false,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/l/:slug/:id/make-offer',
      name: 'MakeOfferPage',
      auth: true,
      component: MakeOfferPage,
      loadData: pageDataLoadingAPI.MakeOfferPage.loadData,
    },
    {
      path: '/l/:slug/:id/request-quote',
      name: 'RequestQuotePage',
      auth: true,
      component: RequestQuotePage,
      extraProps: { mode: 'request-quote' },
      loadData: pageDataLoadingAPI.RequestQuotePage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          // 'photos', not 'details': the new-listing wizard was reordered to
          // photos-first, and this redirect was never updated. Pointing at
          // details meant /l/new landed on an inactive tab, bounced again to
          // photos, and logged "tried to access a tab which was not yet
          // activated" on every visit. /l/new is the shareable entry point
          // (e.g. in seller emails), so it should go straight there.
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'photos' }}
        />
      ),
    },
    {
      // The shareable "start selling" address — used in seller emails and
      // anywhere a human has to read or type the link. It sits alongside
      // /login and /signup as a plain English word, per Guro's ask, instead of
      // the template's /l/new which reads as an implementation detail.
      // /l/new stays as the internal NewListingPage route; both land here.
      path: '/sell',
      name: 'SellPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'photos' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      // Zuprize Partner, entered as "Butikker". Without a verified partner it
      // shows only the intro and the invitation. The old /partner addresses
      // answer with a permanent redirect here (server/index.js).
      path: '/butikker',
      name: 'PartnerPage',
      component: PartnerPage,
      loadData: pageDataLoadingAPI.PartnerPage.loadData,
    },
    {
      // Each Zuprize Partner's own withdrawal form, linked from the footer.
      path: '/angreskjema',
      name: 'AngreskjemaPage',
      component: AngreskjemaPage,
      loadData: pageDataLoadingAPI.AngreskjemaPage.loadData,
    },
    {
      path: '/butikker/:partnerId',
      name: 'PartnerDetailsPage',
      component: PartnerPage,
      loadData: pageDataLoadingAPI.PartnerPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      // Email + password login form with a "Logg inn med Vipps" option
      // below the divider. Cross-link "Ny bruker? Opprett konto" routes
      // to /signup (the welcome / email-gateway entry).
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      // Welcome / email-gateway entry — Vipps + Google + Facebook +
      // email field with a Continue button that hands off to
      // /signup/complete with ?email=… pre-filled.
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'welcome' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      // Full signup form (Fornavn / Etternavn / E-post / Passord)
      // reached after the welcome step. Email pre-fills from the
      // ?email=… query the welcome form attaches.
      path: '/signup/complete',
      name: 'SignupCompletePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      // Post-signup celebration / onboarding checklist page.
      // Redirected to from the signup flow on success; the user can
      // also reach it directly from any link that points here.
      path: '/get-started',
      name: 'GetStartedPage',
      component: AuthenticationPage,
      extraProps: { tab: 'onboarding' },
      // The welcome is a dialog over the homepage, so the homepage's data.
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      // Open to guests: the favorites duck reads from localStorage
      // when the user isn't signed in, so anyone can browse what
      // they've hearted on this device. Sign-in merges that local
      // list into the user's profile (see [[favorites-duck]]).
      path: '/favorites',
      name: 'FavoriteListingsPage',
      ...authForPrivateMarketplace,
      component: FavoriteListingsPage,
      loadData: pageDataLoadingAPI.FavoriteListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      // Admin-only. What the scheduled jobs are reacting to, in one read-only
      // page; same access pattern as the other admin pages.
      path: '/admin/oversikt',
      name: 'AdminOverviewPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminOverviewPage,
      loadData: pageDataLoadingAPI.AdminOverviewPage.loadData,
    },
    {
      // Admin-only. The page itself redirects a non-admin to the landing
      // page; `auth: true` only keeps logged-out visitors at the login form.
      path: '/admin/banners',
      name: 'BannerAdminPage',
      auth: true,
      authPage: 'LoginPage',
      component: BannerAdminPage,
      loadData: pageDataLoadingAPI.BannerAdminPage.loadData,
    },
    {
      // Admin-only review queue for listings awaiting approval. Same access
      // pattern as /admin/banners; the API checks the role again server-side.
      path: '/admin/listings',
      name: 'ListingAdminPage',
      auth: true,
      authPage: 'LoginPage',
      component: ListingAdminPage,
      loadData: pageDataLoadingAPI.ListingAdminPage.loadData,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/account/manage',
      name: 'ManageAccountPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageAccountPage,
    },
    {
      path: '/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    {
      // The sign-up campaign's landing page, also linked from the footer.
      path: '/kom-i-gang',
      name: 'BliMedlemPage',
      component: BliMedlemPage,
    },
    {
      path: '/bli-medlem',
      name: 'BliMedlemRedirect',
      component: RedirectToKomIGangPage,
    },
    {
      path: '/brukervilkar',
      name: 'UserTermsPage',
      component: UserTermsPage,
    },
    {
      path: '/faq',
      name: 'FaqPage',
      component: FaqPage,
    },
    {
      path: '/how-to-buy',
      name: 'HowToBuyPage',
      component: HowToBuyPage,
    },
    {
      path: '/how-to-sell',
      name: 'HowToSellPage',
      component: HowToSellPage,
    },
    {
      path: '/cookies',
      name: 'CookiesPage',
      component: CookiesPage,
    },
    {
      path: '/contact',
      name: 'ContactPage',
      component: ContactPage,
    },
    {
      path: '/vurderinger',
      name: 'VurderingerPage',
      component: VurderingerPage,
    },
    {
      // Editorial / "Zuprize Magasin" index — lists the editorials
      // authored inline in `src/config/editorials.js`.
      path: '/magasin',
      name: 'MagasinPage',
      component: MagasinPage,
    },
    {
      // Individual editorial article — `:slug` resolves against
      // `editorials.js`; an unknown slug renders the NotFoundPage.
      path: '/magasin/:slug',
      name: 'MagasinArticlePage',
      component: MagasinArticlePage,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: RedirectToUserTermsPage,
    },
    {
      // Norwegian URL (preferred by the client / better Norwegian SEO).
      // The English `/privacy-policy` route below renders the exact
      // same page so either form works.
      path: '/personvern',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      // Sharetribe-stable English URL kept around so existing inbound
      // links keep working. The server answers it with a 301 to
      // /personvern (one address for search engines); this route only
      // serves in-app navigation.
      path: '/privacy-policy',
      name: 'PrivacyPolicyPageEn',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
  ];
};

export default routeConfiguration;
